import React, { memo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Popover,
  SvgIcon,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import { BarChart } from "@mui/icons-material";
import { DatePicker } from "rsuite";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import "rsuite/dist/rsuite.min.css";

const chartInitialValue = {
  chartType: null,
  startDate: null,
  endDate: null,
};

const chartValidationSchema = Yup.object().shape({
  chartType: Yup.object()
    .nullable()
    .test(
      "is-not-empty",
      "Type is required",
      (value) => value && Object.keys(value).length > 0
    ),
  startDate: Yup.date()
    .required("Start date is required")
    .typeError("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .typeError("End date is required")
    .min(Yup.ref("startDate"), "End date cannot be before the start date"),
});

const ChartFilter = memo(
  ({ chartTypes = [], handleGetChartDetails = () => {} }) => {
    const colorSet = useSelector((state) => state.color);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [anchorEl, setAnchorEl] = useState(null);
    const [savedFilters, setSavedFilters] = useState(chartInitialValue);

    const open = Boolean(anchorEl);

    const handleSubmit = (values) => {
      setSavedFilters(values);
      handleGetChartDetails(values);
      setAnchorEl(null);
    };

    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <IconButton
          color="default"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <BarChartIcon fontSize="small" />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              p: isMobile ? 2 : 3,
              width: isMobile ? "90vw" : 400,
              maxWidth: "100%",
            },
          }}
        >
          <Formik
            enableReinitialize
            initialValues={savedFilters}
            validationSchema={chartValidationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => {
              const { values, touched, errors, setFieldValue, handleSubmit } =
                formik;

              return (
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    {/* Chart Type */}
                    <Autocomplete
                      id="chartType"
                      name="chartType"
                      options={chartTypes}
                      getOptionLabel={(option) => option.label || ""}
                      value={values?.chartType || null}
                      onChange={(event, newValue) => {
                        setFieldValue("chartType", newValue || null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Type"
                          error={touched.chartType && !!errors.chartType}
                          helperText={
                            touched.chartType && errors.chartType
                              ? errors.chartType
                              : ""
                          }
                        />
                      )}
                    />

                    <Box>
                      <DatePicker
                        placeholder="Start Date"
                        name="startDate"
                        onChange={(value) => {
                          setFieldValue("startDate", value || null);
                        }}
                        value={values?.startDate || null}
                        size="lg"
                        style={{ width: "100%" }}
                        oneTap
                        cleanable
                        placement="autoVertical"
                      />
                      <ErrorMessage
                        name="startDate"
                        component="div"
                        style={{ color: "red", fontSize: 12 }}
                      />
                    </Box>

                    {/* End Date */}
                    <Box>
                      <DatePicker
                        placeholder="End Date"
                        name="endDate"
                        onChange={(value) => {
                          setFieldValue("endDate", value || null);
                        }}
                        value={values?.endDate || null}
                        size="lg"
                        style={{ width: "100%" }}
                        oneTap
                        cleanable
                        shouldDisableDate={(date) => {
                          const startDate = values?.startDate;
                          return startDate ? date < new Date(startDate) : false;
                        }}
                        placement="autoVertical"
                      />
                      <ErrorMessage
                        name="endDate"
                        component="div"
                        style={{ color: "red", fontSize: 12 }}
                      />
                    </Box>

                    {/* Submit Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      startIcon={
                        <SvgIcon>
                          <BarChart />
                        </SvgIcon>
                      }
                      sx={{
                        backgroundColor: colorSet.color,
                        color: "white",
                        ":hover": { backgroundColor: colorSet.color },
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 500,
                      }}
                    >
                      Generate Chart
                    </Button>
                  </Stack>
                </form>
              );
            }}
          </Formik>
        </Popover>
      </Box>
    );
  }
);

export default ChartFilter;
